import { useCallback, useState } from "react";
import { NodeToolbar, Position } from "@xyflow/react";
import { Colors } from "@zapier/design-system";
import { Icon } from "@zapier/zinnia-icons";
import classNames from "classnames";
import { GroupNodeColorPicker } from "../GroupNodeColorPicker";
import { useDeleteNodes } from "../../../../../hooks/useDeleteNodes";
import { ButtonOrLinkWithTooltip } from "../../../../ButtonOrLinkWithTooltip";
import styles from "./GroupNodeFloatingToolbar.module.css";

type Props = {
  id: string;
  color: string;
};

type ActiveButtonType = "color";

export function GroupNodeFloatingToolbar({ id, color }: Props) {
  const [activeButton, setActiveButton] = useState<ActiveButtonType | null>(
    null,
  );
  const { onNodesDelete } = useDeleteNodes();

  const onButtonClick = useCallback(
    (type: ActiveButtonType) => setActiveButton(type),
    [setActiveButton],
  );

  const returnToMainToolbar = useCallback(
    () => setActiveButton(null),
    [setActiveButton],
  );

  const handleDelete = useCallback(() => {
    if (id) {
      onNodesDelete([{ id, position: { x: 0, y: 0 }, data: {} }]);
    }
  }, [onNodesDelete, id]);

  const componentMapByType = {
    color: (
      <GroupNodeColorPicker
        id={id}
        color={color!}
        onClose={returnToMainToolbar}
      />
    ),
  };

  return (
    <NodeToolbar position={Position.Top} offset={14}>
      {activeButton ? (
        componentMapByType[activeButton]
      ) : (
        <div className={styles.floatingToolbarWrapper}>
          <ButtonOrLinkWithTooltip
            content="Edit color"
            className={classNames(styles.buttonNoStyle, styles.firstButton)}
            onClick={() => onButtonClick("color")}
          >
            <div
              className={classNames(styles.colorDot, {
                [styles.colorDotWhite]: color === Colors.PrimeWhite,
              })}
              style={{ backgroundColor: color }}
            />
          </ButtonOrLinkWithTooltip>
          <ButtonOrLinkWithTooltip
            content="Delete group (not steps inside)"
            className={classNames(styles.buttonNoStyle, styles.lastButton)}
            onClick={handleDelete}
          >
            <Icon name="actionTrash" size={20} />
          </ButtonOrLinkWithTooltip>
        </div>
      )}
    </NodeToolbar>
  );
}
